import React, { useEffect, useState } from "react";
import { message } from "antd";

interface EnterpriseModalProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  children?: React.ReactNode;
  width?: number;
  enterpriseCount?: number;
}

const EnterpriseModal: React.FC<EnterpriseModalProps> = ({
  visible,
  title,
  onClose,
  children,
  width = 540,
  enterpriseCount,
}) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [subscribeType, setSubscribeType] = useState('once');
  const [deliveryChannels, setDeliveryChannels] = useState<string[]>(['wechat']);

  const subscribeOptions = [
    { value: 'once', label: '单次推送', desc: '仅推送本次查询结果' },
    { value: 'daily', label: '每日推送', desc: '每日定时推送最新数据' },
    { value: 'weekly', label: '每周推送', desc: '每周一推送汇总报告' },
  ];

  const deliveryOptions = [
    { value: 'wechat', label: '微信', icon: 'ri-wechat-line', color: 'text-green-500' },
    { value: 'email', label: '邮箱', icon: 'ri-mail-line', color: 'text-blue-500' },
    { value: 'sms', label: '手机短信', icon: 'ri-smartphone-line', color: 'text-orange-500' },
  ];

  const count = enterpriseCount || 1;
  const estimatedTokens = count * deliveryChannels.length * 3000;

  const toggleChannel = (ch: string) => {
    setDeliveryChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  };

  // 检测 AI 对话框是否打开（chat-open class 由 index.html 中的脚本维护）
  useEffect(() => {
    if (!visible) return;
    // 立即读取一次，避免闪烁
    const btn = document.getElementById('fastgpt-chatbot-button');
    setChatOpen(btn?.classList.contains('chat-open') ?? false);
    // 持续监听状态变化
    const check = setInterval(() => {
      const b = document.getElementById('fastgpt-chatbot-button');
      setChatOpen(b?.classList.contains('chat-open') ?? false);
    }, 300);
    return () => clearInterval(check);
  }, [visible]);

  // ESC 键关闭
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 flex items-center"
      style={{
        backgroundColor: "rgba(0,0,0,0.45)",
        zIndex: 2147483646,
        justifyContent: chatOpen ? "flex-start" : "center",
        paddingLeft: chatOpen ? 80 : 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{ width, height: 520 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <i className="ri-bar-chart-grouped-line text-blue-500 text-sm" />
            <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPushModalOpen(true)}
              className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-colors cursor-pointer flex items-center gap-1 mr-2"
            >
              <i className="ri-send-plane-line text-[13px]" />
              商机推送
            </button>
            <button
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {children}
        </div>
      </div>

      {/* === 商机推送弹窗 === */}
      {pushModalOpen && (
        <div className="fixed inset-0 z-[2147483647] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setPushModalOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-[480px] max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-2">
                <i className="ri-send-plane-line text-blue-500 text-base" />
                <h3 className="text-sm font-semibold text-slate-700">商机推送</h3>
              </div>
              <button
                onClick={() => setPushModalOpen(false)}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-loop-left-line text-blue-400 text-[13px]" />
                  <span className="text-xs font-semibold text-slate-600">订阅方式</span>
                </div>
                <div className="space-y-2">
                  {subscribeOptions.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                        subscribeType === opt.value
                          ? 'border-blue-400 bg-blue-50/50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="subscribeType"
                        value={opt.value}
                        checked={subscribeType === opt.value}
                        onChange={e => setSubscribeType(e.target.value)}
                        className="mt-0.5 accent-blue-500"
                      />
                      <div>
                        <div className="text-xs font-medium text-slate-700">{opt.label}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-share-line text-blue-400 text-[13px]" />
                  <span className="text-xs font-semibold text-slate-600">发送方式</span>
                </div>
                <div className="flex gap-2">
                  {deliveryOptions.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex-1 flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border cursor-pointer transition-all ${
                        deliveryChannels.includes(opt.value)
                          ? 'border-blue-400 bg-blue-50/50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={deliveryChannels.includes(opt.value)}
                        onChange={() => toggleChannel(opt.value)}
                        className="hidden"
                      />
                      <i className={`${opt.icon} ${opt.color} text-lg`} />
                      <span className="text-xs text-slate-600">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg px-4 py-3 border border-amber-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-coin-line text-amber-500 text-[13px]" />
                    <span className="text-xs font-medium text-amber-700">预计消耗 Token</span>
                  </div>
                  <span className="text-sm font-bold text-amber-600">{estimatedTokens.toLocaleString()}</span>
                </div>
                <div className="mt-1.5 text-[11px] text-amber-500">
                  {count} 条数据 × {deliveryChannels.length} 个渠道 × 3,000 token/条
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-3">
              <button
                onClick={() => setPushModalOpen(false)}
                className="flex-1 py-2 rounded-lg border border-slate-300 bg-white text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setPushModalOpen(false);
                  message.success('推送成功');
                }}
                className="flex-1 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-send-plane-line text-base" />
                确认推送
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseModal;
